import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Activity, Plus, Siren, ChevronRight } from 'lucide-react';
import { Container, PageHeader, Section } from '@vnetwork/ui/layout';
import { Stat, StatGroup, StatusPill, EmptyState, DataTable } from '@vnetwork/ui/data';
import type { ColumnDef } from '@vnetwork/ui/data';
import type { AlarmEvent } from '@/api/healthcheck';
import { Button, Badge, Skeleton } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useWatchpoints, useAlarmEvents } from '@/api/healthcheck';
import {
  typeMeta,
  statusTone,
  statusLabel,
  severityTone,
  targetSummary,
  fmtRelTime,
} from './shared';
import { incidentSummary } from './incidents/incident-context';
import { FirstRunChecklist } from './onboarding';

export function MonitorsDashboard() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const base = `/services/healthcheck/${projectId}`;

  const { data: wp, isLoading, isError, refetch } = useWatchpoints(projectId);
  const monitors = wp?.items ?? [];
  // Alarm-events are tenant-wide (no project filter on the API), so scope them to
  // this project's monitors client-side via watchpoint_id.
  const { data: triggered } = useAlarmEvents({ status: 'triggered', page_size: 100 });

  const monitorIds = useMemo(() => new Set(monitors.map((m) => m.id)), [monitors]);
  const monitorById = useMemo(() => new Map(monitors.map((m) => [m.id, m])), [monitors]);

  const stats = useMemo(() => {
    let up = 0;
    let down = 0;
    for (const m of monitors) {
      if (!m.is_active) continue;
      const s = String(m.last_status ?? '').toLowerCase();
      if (s === 'up') up += 1;
      else if (s === 'down') down += 1;
    }
    return { total: monitors.length, up, down };
  }, [monitors]);

  const incidents = useMemo(
    () => (triggered?.items ?? []).filter((e) => monitorIds.has(e.watchpoint_id)),
    [triggered, monitorIds],
  );

  const incidentColumns = useMemo<ColumnDef<AlarmEvent, unknown>[]>(
    () => [
      {
        id: 'severity',
        header: 'Severity',
        enableSorting: false,
        cell: ({ row }) => {
          const sev = row.original.severity ?? 'warning';
          return (
            <Badge tone={severityTone(sev)} appearance="subtle" size="sm" style={{ textTransform: 'capitalize' }}>
              {sev}
            </Badge>
          );
        },
      },
      {
        id: 'monitor',
        header: 'Monitor',
        enableSorting: false,
        cell: ({ row }) => {
          const m = monitorById.get(row.original.watchpoint_id);
          const tm = typeMeta(m?.type ?? 'http');
          const Icon = tm.icon;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', minWidth: 0 }}>
              <Icon size={15} style={{ flexShrink: 0, color: 'var(--vn-fg-muted)' }} />
              <span style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m?.name ?? 'Monitor'}</span>
              <Badge tone="neutral" appearance="subtle" size="sm">{tm.label}</Badge>
            </div>
          );
        },
      },
      {
        id: 'detail',
        header: 'Detail',
        enableSorting: false,
        cell: ({ row }) => {
          const m = monitorById.get(row.original.watchpoint_id);
          return (
            <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)', display: 'inline-block', maxWidth: 320, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {incidentSummary(m?.type ?? 'http', row.original.details, row.original.matched_items)}
            </span>
          );
        },
      },
      {
        id: 'triggered',
        header: 'Triggered',
        enableSorting: false,
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{fmtRelTime(row.original.triggered_at)}</span>
        ),
      },
    ],
    [monitorById],
  );

  // State C — project has no monitors yet: show the first-run checklist instead of
  // the (all-zero) stats + empty sections. Keeps the page chrome minimal so the
  // onboarding guide is the focus.
  const isFirstRun = !isLoading && !isError && monitors.length === 0;
  if (isFirstRun) {
    return (
      <Container size="xl">
        <PageHeader
          icon={<Activity size={22} />}
          title={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
              Monitoring overview
              <Badge tone="info" appearance="subtle" size="sm">
                Beta
              </Badge>
            </span>
          }
          description="Uptime, latency and incidents across this project's monitors."
        />
        <FirstRunChecklist />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        icon={<Activity size={22} />}
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            Monitoring overview
            <Badge tone="info" appearance="subtle" size="sm">
              Beta
            </Badge>
          </span>
        }
        description="Uptime, latency and incidents across this project's monitors."
        actions={
          <Button size="sm" startIcon={<Plus size={15} />} onClick={() => navigate(`${base}/monitors/create`)}>
            New monitor
          </Button>
        }
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <StatGroup columns={4}>
          <Stat label="Monitors" value={isLoading ? '—' : stats.total} />
          <Stat label="Up" value={isLoading ? '—' : stats.up} dotColor="var(--vn-success-solid)" />
          <Stat label="Down" value={isLoading ? '—' : stats.down} dotColor="var(--vn-danger-solid)" />
          <Stat
            label="Active incidents"
            value={incidents.length}
            dotColor="var(--vn-warning-solid)"
          />
        </StatGroup>

        <Section
          style={{ marginBottom: 0 }}
          title="Active incidents"
          description="Triggered alarms awaiting acknowledgement."
          actions={
            <Button variant="ghost" size="sm" endIcon={<ChevronRight size={14} />} onClick={() => navigate(`${base}/incidents`)}>
              View all
            </Button>
          }
        >
          <DataTable
            columns={incidentColumns}
            data={incidents}
            getRowId={(e) => e.id}
            onRowClick={(e) => {
              const m = monitorById.get(e.watchpoint_id);
              if (m) navigate(`${base}/monitors/${m.id}`);
            }}
            emptyState={
              <EmptyState icon={<Siren size={26} />} title="No active incidents" description="All monitors are healthy." size="sm" />
            }
          />
        </Section>

        <Section
          style={{ marginBottom: 0 }}
          title="Monitors"
          description="Most recent watchpoints in this project."
          actions={
            <Button variant="ghost" size="sm" endIcon={<ChevronRight size={14} />} onClick={() => navigate(`${base}/monitors`)}>
              View all
            </Button>
          }
        >
          {isError ? (
            <Alert
              tone="danger"
              title="Couldn't load monitors"
              actions={
                <Button size="sm" variant="secondary" onClick={() => refetch()}>
                  Retry
                </Button>
              }
            >
              The monitoring service is temporarily unavailable.
            </Alert>
          ) : isLoading ? (
            <div style={grid}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} style={{ height: 96 }} />
              ))}
            </div>
          ) : monitors.length === 0 ? (
            <EmptyState
              icon={<Activity size={28} />}
              title="No monitors yet"
              description="Create your first watchpoint to start tracking uptime and latency."
              actions={
                <Button size="sm" startIcon={<Plus size={15} />} onClick={() => navigate(`${base}/monitors/create`)}>
                  New monitor
                </Button>
              }
            />
          ) : (
            <div style={grid}>
              {monitors.slice(0, 6).map((m) => {
                const meta = typeMeta(m.type);
                const Icon = meta.icon;
                return (
                  <button key={m.id} type="button" style={card} onClick={() => navigate(`${base}/monitors/${m.id}`)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', marginBottom: 'var(--vn-space-2)' }}>
                      <Icon size={18} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {m.name}
                        </div>
                        <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {targetSummary(m.type, m.config)}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <StatusPill tone={statusTone(m.is_active ? m.last_status : null)}>
                        {statusLabel(m.last_status, m.is_active)}
                      </StatusPill>
                      <Badge tone="info" appearance="subtle" size="sm">
                        {meta.label}
                      </Badge>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Section>
      </div>
    </Container>
  );
}

const grid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: 'var(--vn-space-3)',
};
const card: React.CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  cursor: 'pointer',
  font: 'inherit',
  color: 'var(--vn-fg-default)',
  border: '1px solid var(--vn-border-default)',
  borderRadius: 'var(--vn-radius-lg)',
  background: 'var(--vn-bg-surface)',
  padding: 'var(--vn-space-4)',
};
